import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';

import CapacityTestConfirmDialog from './CapacityTestConfirmDialog.js';
import CapacityTestResults from './CapacityTestResults.js';
import {
  hasActiveCapacityTest,
  RoutersApiKeys,
  useCancelCapacityTest,
  useRouterCapacityTests,
  useStartCapacityTest,
} from '../../../api/index.js';
import { apiErrorMessage } from '../../../utils/api-error-message.js';

interface RouterCapacityTestTabProps {
  routerId: string;
  canRunTest: boolean;
}

const RouterCapacityTestTab: FC<RouterCapacityTestTabProps> = ({ routerId, canRunTest }) => {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data: tests = [], isLoading } = useRouterCapacityTests(routerId);
  const startTest = useStartCapacityTest(routerId);
  const cancelTest = useCancelCapacityTest(routerId);

  const activeTest = tests.find((test) => test.status === 'queued' || test.status === 'running');
  const finishedTests = tests.filter((test) => test !== activeTest);
  const refreshTests = () => queryClient.invalidateQueries([RoutersApiKeys.Routers, routerId, RoutersApiKeys.CapacityTests]);

  const onConfirm = () => {
    startTest.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        void refreshTests();
      },
    });
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    startTest.reset();
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Pulls real video from the cameras through the router at several quality profiles, then adds streams one
            at a time until frames start dropping. Tells you which profiles stream smoothly and how many HQ streams
            the active SIM can carry at once.
          </Typography>
          {canRunTest ? (
            <Button
              variant="contained"
              startIcon={<NetworkCheckIcon />}
              disabled={isLoading || hasActiveCapacityTest(tests)}
              onClick={() => setConfirmOpen(true)}
            >
              Run network test
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {activeTest ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              {activeTest.status === 'queued'
                ? 'Network test queued'
                : `Network test running · ${activeTest.results.length} steps done`}
            </Typography>
            {canRunTest ? (
              <LoadingButton
                color="error"
                loading={cancelTest.isLoading}
                onClick={() => cancelTest.mutate(activeTest.id, { onSuccess: () => void refreshTests() })}
              >
                Cancel
              </LoadingButton>
            ) : null}
          </Stack>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Video from this router is paused until the test finishes.
          </Typography>
        </Paper>
      ) : null}

      {cancelTest.isError ? (
        <Alert severity="error">{apiErrorMessage(cancelTest.error, 'The test could not be cancelled')}</Alert>
      ) : null}

      {!isLoading && tests.length === 0 ? (
        <Typography color="text.secondary">This router has not been tested yet.</Typography>
      ) : null}

      {finishedTests.map((test) => <CapacityTestResults key={test.id} test={test} />)}

      <CapacityTestConfirmDialog
        open={confirmOpen}
        routerId={routerId}
        starting={startTest.isLoading}
        startError={startTest.isError ? apiErrorMessage(startTest.error, 'The test could not be started') : null}
        onConfirm={onConfirm}
        onCancel={closeConfirm}
      />
    </Stack>
  );
};

export default RouterCapacityTestTab;
